import React from 'react';
import { connect } from 'react-redux';
import { FormattedNumber, FormattedPlural, FormattedMessage } from 'react-intl';
import { Typography } from '@material-ui/core';

/**
 * Prices displaying components
 */

const YEAR_UNIT = 'year';
const MONTH_UNIT = 'month';

const MONTH_PER_YEAR = 12;

/**
 * Calculates the price of a subscription
 */
const calculateSubscriptionPrice = ({ plan, licencesCount }) => {
  let price = plan.price;
  const extraLicences = licencesCount - plan.licencesCount;
  if (extraLicences > 0) {
    price += extraLicences * plan.extraLicencePrice;
  }
  return price;
}

/**
 * Returns true if the price for one licence is the same for
 * the plan and for the extra licences
 * @param {Object} props containing a plan 
 */
const isLinearPrice = ({ plan }) => {
  let basePricePerLicence = plan.price / plan.licencesCount;
  return basePricePerLicence === plan.extraLicencePrice;
}

/**
 * Returns {value, unit} such as biggest unit is displayed,
 * for example {value: 24, unit: month} would become {value: 2, unit: year}
 * @param {Object} props {value, unit}
 */
const normalizeDuration = ({ value, unit }) => {
  if (value % MONTH_PER_YEAR === 0 && unit === MONTH_UNIT) {
    return { value: value / MONTH_PER_YEAR, unit: YEAR_UNIT };
  }
  return { value, unit };
}

/**
 * Displays the periodicity of a plan or subscription, 
 * typically after a "/"
 * Hides 1 so it doesn't read "30€ / 1 month"
 * @param {*} props with value and unit 
 */
export const Periodicity = (periodicity) => {
  const { value, unit } = normalizeDuration(periodicity);
  return (
    <span>
      {value > 1 && <FormattedNumber value={value} />}
      {" "}
      <FormattedPlural
        value={value}
        one={
          <FormattedMessage id={'duration.unit.' + unit} />
        }
        other={
          <FormattedMessage id={'duration.unit.' + unit + '.plural'} />
        }
      />
    </span>
  );
}

/**
 * Displays a duration
 * @param {*} props with value and unit 
 */
export const Duration = (duration) => {
  const { value, unit } = normalizeDuration(duration);
  return (
    <span>
      <FormattedNumber value={value} />
      {" "}
      <FormattedPlural
        value={value}
        one={
          <FormattedMessage id={'duration.unit.' + unit} />
        }
        other={
          <FormattedMessage id={'duration.unit.' + unit + '.plural'} />
        } />
    </span>
  );
}

/**
 * Formats a price in cents to a price in units
 * @param {*} props value is in cents
 */
const Price = ({ value, ...props }) => (
  <FormattedNumber
    value={value / 100}
    // eslint-disable-next-line  
    style="currency"
    {...props} />
)

/**
 * Formats a price in cents to a price in units without decimal parts if not necessary 
 * The result is $50 or $50.99 regarding the value props input
 * @param {*} props value is in cents
 */
const PriceComponent = ({ value, currency, ...props }) => (
  <Price
    value={value}
    currency={currency.code}
    minimumFractionDigits={0}
  />
)

/**
 * Formats the base price for a plan
 * @param {*} props 
 */
const PlanBasePrice = ({ plan, currency }) => (
  <span>
    <FormattedMessage
      id='plan.base.price'
      values={{
        licencesCount: plan.licencesCount,
        price: <Price
          value={plan.price}
          currency={currency.code}
        />,
        periodicity: <Periodicity
          value={plan.periodicity}
          unit={plan.periodicityUnit}
        />
      }}
    />
  </span>
)

/**
 * Formats the price per licence for a plance
 * @param {*} props with plan, currency and unit 
 */
const PlanLicencePrice = ({ plan, currency, unit = "licence" }) => (
  <span>
    <FormattedMessage
      id='plan.licence.price'
      values={{
        price: <Price
          value={plan.extraLicencePrice}
          currency={currency.code}
        />,
        periodicity: <Periodicity
          value={plan.periodicity}
          unit={plan.periodicityUnit}
        />,
        unit: <FormattedMessage id={unit + '.unit'} />
      }}
    />
  </span>
)

/**
 * Formats the price of a plan
 * @param {*} props with plan and currency
 */
const PlanPriceComponent = ({ plan, currency }) => (
  isLinearPrice
    ? <PlanLicencePrice plan={plan} currency={currency} />
    : <FormattedMessage
      id='plan.price'
      values={{
        basePrice: <PlanBasePrice plan={plan} currency={currency} />,
        extraLicencePrice: <PlanLicencePrice plan={plan} currency={currency} unit="extraLicence" />
      }}
    />
)

/**
 * Customized PlanPriceComponent
 * @param {} param0 
 */
const CustomizedPlanPriceComponent = ({ plan, currency }) => (
  isLinearPrice
    ? <CustomizedPlanLicencePrice plan={plan} currency={currency} />
    : <FormattedMessage
      id='plan.price'
      values={{
        basePrice: <CustomizedPlanBasePrice plan={plan} currency={currency} />,
        extraLicencePrice: <CustomizedPlanLicencePrice plan={plan} currency={currency} unit="extraLicence" />
      }}
    />
)

/**
 * Formats the price per licence for a plance
 * @param {*} props with plan, currency and unit 
 */
const CustomizedPlanLicencePrice = ({ plan, currency, unit = "licence" }) => (
  <span>
    <FormattedMessage
      id='plan.licence.price'
      values={{
        price: 
          <Typography inline={true} variant='h5' style={{fontWeight: 'bold'}}>
            <PriceComponent
              value={!!plan.extraLicencePrice ? plan.extraLicencePrice : plan.price}
              currency={currency}
              minimumFractionDigits={0}
            />
          </Typography>,
        periodicity: 
          <Typography inline={true} variant='body2' >
            <Periodicity
              value={plan.periodicity}
              unit={plan.periodicityUnit}
            />
          </Typography>,
        unit: 
          <Typography inline={true} variant='body2' >
            <FormattedMessage id={unit + '.unit'} />
          </Typography>
      }}
    />
  </span>
)

/**
 * Formats the base price for a plan
 * @param {*} props 
 */
const CustomizedPlanBasePrice = ({ plan, currency }) => (
  <span>
    <FormattedMessage
      id='plan.base.price'
      values={{
        licencesCount: plan.licencesCount,
        price: 
          <Typography inline={true} variant='h5' style={{fontWeight: 'bold'}}>
            <PriceComponent
              value={plan.price}
              currency={currency}
              minimumFractionDigits={0}
            />
          </Typography>,
        periodicity: 
          <Typography inline={true} variant='body2' >
            <Periodicity
              value={plan.periodicity}
              unit={plan.periodicityUnit}
            />
          </Typography>
      }}
    />
  </span>
)

/**
 * Formats the price of a subscription
 * @param {*} props with subscription and currency 
 */
const SubscriptionPriceComponent = ({ subscription, currency }) => (
  subscription && subscription.plan
    ? <FormattedMessage
      id='subscription.price'
      values={{
        price: <Price
          value={calculateSubscriptionPrice(subscription)}
          currency={currency.code}
        />,
        periodicity: <Periodicity
          value={subscription.plan.periodicity}
          unit={subscription.plan.periodicityUnit}
        />
      }}
    />
    : <FormattedMessage id='subscription.price.unknown' />
)

/**
 * Formats the price of a subscription, as displayed when creating it from a plan
 * @param {*} props 
 */
const PlanSubscriptionPriceComponent = ({ plan, currency, licencesCount }) => (
  plan
    ? <FormattedMessage
      id='subscription.price'
      values={{
        price: <Price
          value={calculateSubscriptionPrice({ plan, licencesCount })}
          currency={currency.code}
        />,
        periodicity: <Periodicity
          value={plan.periodicity}
          unit={plan.periodicityUnit}
        />
      }}
    />
    : <FormattedMessage id='subscription.price.unknown' />
)

/**
 * Map state currency to components
 */
const mapStateToProps = state => ({
  currency: state.currency
})

export let SimplePrice = connect(
  mapStateToProps
)(PriceComponent);

export let PlanPrice = connect(
  mapStateToProps
)(PlanPriceComponent);

export let PlanSubscriptionPrice = connect(
  mapStateToProps
)(PlanSubscriptionPriceComponent)

export let SubscriptionPrice = connect(
  mapStateToProps
)(SubscriptionPriceComponent)

export let CustomizedPlanPrice = connect(
  mapStateToProps
)(CustomizedPlanPriceComponent);


// WEBPACK FOOTER //
// ./src/components/Prices.js